<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <form-card :title="$t(tKey + '.general.title')">
            <template #description>
              <p class="text-neutral">
                {{ $t(tKey + '.general.description') }}
              </p>
              <custom-button
                class="mt-4"
                :variant="calculateButtonEnabled ? 'primary' : 'outlined'"
                :disabled="!calculateButtonEnabled"
                @click="calculateFields"
              >
                {{ $t(tKey + '.general.calculateButton') }}
              </custom-button>
            </template>
            <div class="flex flex-col gap-y-5">
              <multi-select
                v-model="rawLicense.company"
                :error="errors?.company"
                :searchable="true"
                :label="$t('licenses.company')"
                :placeholder="$t('licenses.company')"
                :options="fetchCompanies"
              />
              <DatePicker
                v-model="rawLicense.startDate"
                :error="errors?.startDate"
                mode="datetime"
                :label="$t('licenses.start')"
                :min-date="minStartDate"
                :disabled="isStartDateDisabled"
              />
              <FormInput
                v-model="rawLicense.duration"
                :error="errors?.duration"
                type="number"
                :label="$t('licenses.duration')"
                :placeholder="$t('licenses.duration')"
              />
              <DatePicker
                v-model="rawLicense.endDate"
                :error="errors?.endDate"
                mode="datetime"
                :label="$t('licenses.end')"
                :min-date="rawLicense.startDate"
              />
              <div class="grid grid-cols-3">
                <Switch v-model="rawLicense.automaticRenewal" :error="errors?.automaticRenewal" :label="$t('licenses.automaticRenewal')" />
                <FormInput
                  v-if="rawLicense.automaticRenewal"
                  v-model="rawLicense.followupLicenseId"
                  :readonly="true"
                  :disabled="true"
                  :error="errors?.followupLicenseId"
                  class="col-span-2"
                  type="text"
                  :label="$t('licenses.followupLicense')"
                  :placeholder="$t('licenses.followupLicense')"
                />
              </div>
            </div>
          </form-card>
          <form-card :title="$t(tKey + '.goal.title')" :description="$t(tKey + '.goal.description')">
            <div class="flex flex-col gap-y-5">
              <multi-select
                v-if="(rawLicense.licenseGoal?.licenseGoalStatements ?? []).length > 1"
                v-model="rawLicense.licenseGoal.formula"
                class="mb-4"
                :error="(errors?.licenseGoal as ValidatedTree)?.formula"
                :searchable="true"
                :label="$t('licenses.licenseGoal.formula')"
                :placeholder="$t('licenses.licenseGoal.formula')"
                :options="fetchGoalFormulaTypes"
              />
              <GoalPicker v-model="rawLicense.licenseGoal.licenseGoalStatements" :error="errors?.licenseGoal?.licenseGoalStatements" />
            </div>
          </form-card>
          <form-card :title="$t(tKey + '.ranking.title')" :description="$t(tKey + '.ranking.description')">
            <div class="flex flex-col gap-y-5">
              <p class="text-body-regular-bold">
                {{ $t(tKey + '.ranking.heading') }}
              </p>
              <RankingPicker v-model="rawLicense.licenseRanking.licenseRankingStatements" :error="errors?.licenseRanking?.licenseRankingStatements" />
            </div>
          </form-card>
          <form-card :title="$t(tKey + '.reward.title')" :description="$t(tKey + '.reward.description')">
            <div class="flex flex-col gap-y-5">
              <RewardPicker v-model="rawLicense.licenseReward.licenseBounties" :error="errors?.licenseReward?.licenseBounties" />
            </div>
          </form-card>
          <form-card :title="$t(tKey + '.payout.title')" :description="$t(tKey + '.payout.description')">
            <div class="flex flex-col gap-y-5">
              <multi-select
                v-model="rawLicense.payoutSchedule.type"
                class="mb-4"
                :searchable="true"
                :error="(errors?.payoutSchedule as ValidatedTree)?.type"
                :label="$t('licenses.schedule.type.label')"
                :placeholder="$t('licenses.schedule.type.label')"
                :options="fetchScheduleTypes"
              />
              <FormInput
                v-if="rawLicense.payoutSchedule?.type === 'monthly'"
                v-model="rawLicense.payoutSchedule.interval"
                type="number"
                :error="(errors?.payoutSchedule as ValidatedTree)?.interval"
                :label="$t('licenses.schedule.interval')"
                :placeholder="$t('licenses.schedule.interval')"
              />
              <div class="flex flex-col gap-y-2">
                <div class="flex items-center gap-x-2">
                  <Slider v-model="rawLicense.payoutSchedule.minimumThreshold" class="basis-10/12 grow" :label="$t('licenses.minimumThreshold')" />
                  <p v-if="minimumThresholdAbsoluteValueFormatted" class="basis-2/12 whitespace-pre mt-4">
                    {{ minimumThresholdAbsoluteValueFormatted }}
                  </p>
                </div>
                <div class="flex items-center gap-x-2">
                  <FormInput
                    v-model="minimumThresholdAbsoluteValue"
                    class="basis-10/12 grow"
                    type="number"
                    :clean-appearance="true"
                  />
                  <MultiSelect
                    v-model="minimumThresholdAbsoluteSetting"
                    class="basis-2/12"
                    :options="absoluteSettingsOptions"
                    :searchable="false"
                    :can-clear="false"
                  />
                </div>
              </div>
              <div class="flex flex-col gap-y-2">
                <div class="flex items-center gap-x-2">
                  <Slider v-model="rawLicense.payoutSchedule.maximumPayout" class="basis-10/12 grow" :label="$t('licenses.maximumPayout')" />
                  <p v-if="maximumPayoutAbsoluteValueFormatted" class="basis-2/12 mt-4">
                    {{ maximumPayoutAbsoluteValueFormatted }}
                  </p>
                </div>
                <div class="flex items-center gap-x-2">
                  <FormInput
                    v-model="maximumPayoutAbsoluteValue"
                    class="basis-10/12 grow"
                    type="number"
                    :clean-appearance="true"
                  />
                  <MultiSelect
                    v-model="maximumPayoutAbsoluteValueSetting"
                    class="basis-2/12"
                    :options="absoluteSettingsOptions"
                    :searchable="false"
                    :can-clear="false"
                  />
                </div>
              </div>
            </div>
          </form-card>
          <form-card :title="$t(tKey + '.seats.title')" :description="$t(tKey + '.seats.description')">
            <p v-if="showSeatsWarning(rawLicense.licenseSeats ?? [])" class="mb-4">
              {{ $t(`${tKey}.seats.seatWarning`) }}
            </p>
            <div class="grid grid-cols-2 gap-y-5 gap-x-5">
              <FormInput
                v-model="rawLicense.seatsBox"
                :error="errors?.seatsBox"
                type="number"
                :label="$t('licenses.seatsBox')"
                :placeholder="$t('licenses.seatsBox')"
              />
              <FormInput
                v-model="rawLicense.seatsNoBox"
                :error="errors?.seatsNoBox"
                type="number"
                :label="$t('licenses.seatsNoBox')"
                :placeholder="$t('licenses.seatsNoBox')"
              />
            </div>
          </form-card>
          <form-card :title="$t(tKey + '.advertisement.title')" :description="$t(tKey + '.advertisement.description')">
            <div class="flex flex-col gap-y-5">
              <Switch v-model="rawLicense.advertisementEnabled" :error="errors?.advertisementEnabled" :label="$t('licenses.advertisementEnabled')" />
              <MultiselectTags
                v-if="rawLicense.advertisementEnabled"
                v-model="rawLicense.blockedAdvertisementCompanies"
                :error="errors?.blockedAdvertisementCompanies"
                :searchable="true"
                :label="$t('licenses.blockedAdvertisementCompanies')"
                :placeholder="$t('licenses.company')"
                :options="fetchCompanies"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, ref, watch,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation, type ValidatedTree } from '@hooks/form';
import {
  object, string, date, number, boolean, array,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import {
  add, isBefore, isPast, startOfDay,
} from 'date-fns';
import type {
  Company,
  CompanyPayoutSchedule,
  GoalStatement,
  License,
  LicenseGoal,
  LicenseRanking,
  LicenseReward,
  LicenseSeat,
  RankingStatement,
} from '@/entities/Entities';
import DatePicker from '@/components/utils/inputs/DatePicker.vue';
import Switch from '@/components/utils/inputs/Switch.vue';
import { collOptionsProvider, enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import MultiSelect from '@/components/utils/Multiselect.vue';
import MultiselectTags from '@/components/utils/MultiselectTags.vue';
import CustomButton from '@/components/utils/CustomButton.vue';
import GoalPicker from '@/components/utils/inputs/licenses/GoalPicker.vue';
import RewardPicker from '@/components/utils/inputs/licenses/RewardPicker.vue';
import Slider from '@/components/utils/inputs/Slider.vue';
import RankingPicker from '@/components/utils/inputs/licenses/RankingPicker.vue';

interface Props {
  open: boolean;
  license?: License;
}

const emit = defineEmits(['update:open', 'update:license', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalLicense = useVModel(props, 'license', emit);

const isNew = computed(() => !props.license);
const tKey = computed(() => (isNew.value ? 'pages.licenseList.modal.new' : 'pages.licenseList.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const getNumberOfSeats = (seats: LicenseSeat[], type: string) =>
  seats
    .filter((seat) => seat.type === type)
    .map((seat) => seat.seats ?? 0)
    .reduce((partial: number, x: number) => partial + x, 0);
const showSeatsWarning = (seats: LicenseSeat[]) => {
  const boxLicenseSeats = seats.filter((seat) => seat.type === 'box').length;
  const noBoxLicenseSeats = seats.filter((seat) => seat.type === 'noBox').length;
  return boxLicenseSeats > 1 || noBoxLicenseSeats > 1;
};

const initialData = (license?: License) =>
  (license
    ? {
      ...license,
      company: license?.company?.['@id'] ?? undefined,
      followupLicenseId: license?.followupLicense?.id ?? undefined,
      licenseGoal: {
        formula: 'average', // only option at the moment
        ...license?.licenseGoal,
        licenseGoalStatements: readGoalStatements(license?.licenseGoal?.licenseGoalStatements),
      } as unknown as LicenseGoal,
      licenseRanking: {
        ...license?.licenseRanking,
        licenseRankingStatements: readRankingStatements(license?.licenseRanking?.licenseRankingStatements),
      } as unknown as LicenseRanking,
      licenseReward: {
        licenseBounties: [],
        ...license?.licenseReward,
      } as unknown as LicenseReward,
      payoutSchedule: {
        type: '',
        interval: 3,
        minimumThreshold: 0,
        maximumPayout: 100,
        ...license?.payoutSchedule,
      } as unknown as CompanyPayoutSchedule,
      blockedAdvertisementCompanies: license?.blockedAdvertisementCompanies?.map((company) => ({
        value: company['@id'],
        label: company.name,
      })),

      seatsBox: getNumberOfSeats(license?.licenseSeats ?? [], 'box'),
      seatsNoBox: getNumberOfSeats(license?.licenseSeats ?? [], 'noBox'),
    }
    : {
      company: undefined,
      startDate: startOfDay(add(new Date(), { days: 1 })),
      endDate: undefined,
      duration: undefined,
      automaticRenewal: false,
      followupLicenseId: undefined,
      transferCompleted: undefined,
      seatsBox: undefined,
      seatsNoBox: undefined,
      licenseSeats: [],
      advertisementEnabled: false,
      blockedAdvertisementCompanies: [],

      licenseGoal: {
        formula: 'average', // only option at the moment
        licenseGoalStatements: [],
      } as unknown as LicenseGoal,
      licenseRanking: {
        licenseRankingStatements: [],
      } as unknown as LicenseRanking,
      licenseReward: {
        licenseBounties: [],
      } as unknown as LicenseReward,
      payoutSchedule: {
        type: '',
        interval: 3,
        minimumThreshold: 0,
        maximumPayout: 100,
      } as unknown as CompanyPayoutSchedule,
    });

const readGoalStatements = (statements: GoalStatement[] | undefined) =>
  statements?.map((statement) => ({
    type: statement.type,
    data: {
      target: statement.type === 'SumOfDistance' ? (statement.data?.target ?? 0) / 1000 : statement.data?.target,
    },
  })) ?? [];
const writeGoalStatements = (statements: GoalStatement[] | undefined) =>
  statements?.map((statement) => ({
    type: statement.type,
    data: {
      target: statement.type === 'SumOfDistance' ? (statement.data?.target ?? 0) * 1000 : statement.data?.target,
    },
  }));

const readRankingStatements = (statements: RankingStatement[] | undefined) => statements?.sort((statement) => statement.position ?? 0) ?? [];
const writeRankingStatements = (statements: RankingStatement[] | undefined) =>
  statements?.map((statement, index) => {
    statement.position = index;
    return statement;
  }) ?? [];

const rawLicense = syncRef(internalLicense, (license) => ({
  ...initialData(license),
}));

const minStartDate = computed(() => add(new Date(), { days: 1 }));
const isStartDateDisabled = computed(() => (rawLicense.value.startDate ? isPast(new Date(rawLicense.value.startDate)) : false));

const {
  data: license,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    company: string().required(t('validation.notEmpty')),
    startDate: date().nullable().required(t('validation.notEmpty')),
    endDate: date().nullable().required(t('validation.notEmpty')),
    duration: number().nullable().required(t('validation.notEmpty')),
    automaticRenewal: boolean().nullable().required(t('validation.notEmpty')),
    followupLicenseId: string().nullable(),
    seatsBox: number().nullable().required(t('validation.notEmpty')),
    seatsNoBox: number().nullable().required(t('validation.notEmpty')),

    licenseGoal: object({
      formula: string().nullable().required(t('validation.notEmpty')),
      licenseGoalStatements: array().nullable().min(1, t('validation.notEmpty')),
    }),

    licenseRanking: object({
      licenseRankingStatements: array().nullable().min(1, t('validation.notEmpty')),
    }),

    licenseReward: object({
      licenseBounties: array().min(1, t('validation.notEmpty')),
    }),

    payoutSchedule: object({
      type: string().nullable().required(t('validation.notEmpty')),
      interval: number().nullable(),
      minimumThreshold: number().required(t('validation.notEmpty')),
      maximumPayout: number().required(t('validation.notEmpty')),
    }),

    advertisementEnabled: boolean().required(t('validation.notEmpty')),
    blockedAdvertisementCompanies: array().nullable(),
  }),
  rawLicense,
);

function resetData() {
  rawLicense.value = { ...initialData() } as unknown as (typeof license)['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalLicense.value = undefined;
}

const [exec, { loading }] = useFetchAction();

const fetchCompanies = collOptionsProvider<Company>(
  '/companies',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
  }),
);
const fetchGoalFormulaTypes = enumOptionsProvider('/enums/GoalFormulaType', (value) => ({
  value,
  label: t(`licenses.licenseGoal.formulaType.${value}`),
}));
const fetchScheduleTypes = enumOptionsProvider('/enums/CompanyPayoutScheduleType', (value) => ({
  value,
  label: t(`licenses.schedule.type.${value}`),
}));

const calculateButtonEnabled = computed(() => !!rawLicense.value.startDate && (!!rawLicense.value.duration || !!rawLicense.value.endDate));
const calculateFields = () => {
  if (!rawLicense.value.startDate) return;

  const startDate = new Date(rawLicense.value.startDate ?? '');

  if (rawLicense.value.duration) {
    const endDate = new Date(startDate.getTime());
    endDate.setMonth(endDate.getMonth() + rawLicense.value.duration);
    rawLicense.value.endDate = endDate;
  }

  if (rawLicense.value.endDate) {
    const endDate = new Date(rawLicense.value.endDate);
    rawLicense.value.duration = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
  }
};

/* Options for which Goal Statement the user sets the absolute values */
const absoluteSettingsOptions = computed(() => {
  if (!rawLicense.value.licenseGoal?.licenseGoalStatements) return '';
  return rawLicense.value.licenseGoal?.licenseGoalStatements?.map((statement) => ({
    label: t(`goalPicker.goalStatementUnits.${statement.type}`),
    value: statement.type,
  }));
});

/* Which type of Goal Statement is used to calculate the absolute value for the minimum threshold */
const minimumThresholdAbsoluteSetting = ref<string | null>(null);
// Watch the Goal Statements and set the minimum threshold absolute setting to the first type of Goal Statement
watch(
  () =>
    rawLicense.value.licenseGoal?.licenseGoalStatements?.reduce((acc: string[], statement) => {
      if (statement.type) {
        acc.push(statement.type);
      }
      return acc;
    }, []),
  (value: string[] | undefined) => {
    if (Array.isArray(value) && value.length > 0) {
      minimumThresholdAbsoluteSetting.value = typeof value[0] === 'string' ? value[0] : null;
    }
  },
  { immediate: true },
);
/* The formatted absolute value for the minimum threshold */
const minimumThresholdAbsoluteValueFormatted = computed(() => {
  if (!rawLicense.value.licenseGoal?.licenseGoalStatements) return '';
  return rawLicense.value.licenseGoal?.licenseGoalStatements
    ?.map((statement) => {
      const target = statement.data?.target ?? 0;
      const part = (target * (rawLicense.value.payoutSchedule?.minimumThreshold ?? 0)) / 100;
      let out = part.toFixed(2);
      if (statement.type) {
        out += ` ${t(`goalPicker.goalStatementUnits.${statement.type}`)}`;
      }
      return out;
    })
    .join('\n');
});
/* The absolute value for the minimum threshold */
const minimumThresholdAbsoluteValue = computed<number>({
  get: () => {
    if (!Array.isArray(rawLicense.value.licenseGoal?.licenseGoalStatements) || rawLicense.value.licenseGoal.licenseGoalStatements.length === 0) {
      return 0;
    }

    for (const statement of rawLicense.value.licenseGoal.licenseGoalStatements) {
      if (statement.type === minimumThresholdAbsoluteSetting.value && statement.data?.target) {
        const absoluteValue = (statement.data.target * (rawLicense.value.payoutSchedule?.minimumThreshold ?? 0)) / 100;
        return Math.round(absoluteValue * 100) / 100;
      }
    }
    return 0;
  },
  set: (val: number) => {
    if (!rawLicense.value.licenseGoal?.licenseGoalStatements) return;
    rawLicense.value.licenseGoal.licenseGoalStatements.forEach((statement) => {
      if (statement.type === minimumThresholdAbsoluteSetting.value && statement.data?.target) {
        const newMinimumThreshold = (val * 100) / (statement.data.target ?? 0);
        rawLicense.value.payoutSchedule.minimumThreshold = newMinimumThreshold;
      }
    });
  },
});

/* Which type of Goal Statement is used to calculate the absolute value for the maximum payout */
const maximumPayoutAbsoluteValueSetting = ref<string | null>(null);
// Watch the Goal Statements and set the maximum payout absolute setting to the first type of Goal Statement
watch(
  () =>
    rawLicense.value.licenseGoal?.licenseGoalStatements?.reduce((acc: string[], statement) => {
      if (statement.type) {
        acc.push(statement.type);
      }
      return acc;
    }, []),
  (value: string[] | undefined) => {
    if (Array.isArray(value) && value.length > 0) {
      maximumPayoutAbsoluteValueSetting.value = typeof value[0] === 'string' ? value[0] : null;
    }
  },
  { immediate: true },
);
/* The formatted absolute value for the maximum payout */
const maximumPayoutAbsoluteValueFormatted = computed<string | undefined>(() => {
  if (!rawLicense.value.licenseGoal?.licenseGoalStatements) return '';
  return rawLicense.value.licenseGoal?.licenseGoalStatements
    ?.map((statement) => {
      const target = statement.data?.target ?? 0;
      const part = (target * (rawLicense.value.payoutSchedule?.maximumPayout ?? 0)) / 100;
      let out = part.toFixed(2);
      if (statement.type) {
        out += ` ${t(`goalPicker.goalStatementUnits.${statement.type}`)}`;
      }
      return out;
    })
    .join('\n');
});
/* The absolute value for the maximum payout */
const maximumPayoutAbsoluteValue = computed<number>({
  get: () => {
    if (!Array.isArray(rawLicense.value.licenseGoal?.licenseGoalStatements) || rawLicense.value.licenseGoal.licenseGoalStatements.length === 0) {
      return 0;
    }

    for (const statement of rawLicense.value.licenseGoal.licenseGoalStatements) {
      if (statement.type === maximumPayoutAbsoluteValueSetting.value && statement.data?.target) {
        const absoluteValue = (statement.data.target * (rawLicense.value.payoutSchedule?.maximumPayout ?? 0)) / 100;
        return Math.round(absoluteValue * 100) / 100;
      }
    }
    return 0;
  },
  set: (val: number) => {
    if (!rawLicense.value.licenseGoal?.licenseGoalStatements) return;
    rawLicense.value.licenseGoal.licenseGoalStatements.forEach((statement) => {
      if (statement.type === maximumPayoutAbsoluteValueSetting.value && statement.data?.target) {
        const newMaximumPayout = (val * 100) / (statement.data.target ?? 0);
        rawLicense.value.payoutSchedule.maximumPayout = newMaximumPayout;
      }
    });
  },
});

const setNumberOfSeats = (l: typeof license, type: string, numberOfSeats: number) => {
  const licenseSeatsOfType = l.value.licenseSeats?.filter((seat) => seat.type === type) ?? [];

  if (licenseSeatsOfType.length === 0) {
    if (numberOfSeats === 0) return;
    l.value.licenseSeats?.push({
      type,
      seats: numberOfSeats,
    }); // id will be created by the server on POST / PUT
  }

  // Set the new number of seats
  licenseSeatsOfType.forEach((seat, index) => {
    seat.seats = Math.floor(numberOfSeats / licenseSeatsOfType.length);
    // Distribute remainder among seats
    if (index < numberOfSeats % licenseSeatsOfType.length) {
      seat.seats += 1;
    }
  });
};

const save = validatedFn(async () => {
  try {
    const edit = !!props.license;
    const url = edit ? props.license['@id'] : '/licenses';

    setNumberOfSeats(license, 'box', rawLicense.value.seatsBox ?? 0);
    setNumberOfSeats(license, 'noBox', rawLicense.value.seatsNoBox ?? 0);

    // Remove empty seats
    license.value.licenseSeats = license.value.licenseSeats?.filter((seat) => (seat.seats ?? 0) > 0);

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        company: license.value.company,
        startDate: license.value.startDate,
        endDate: license.value.endDate,
        duration: license.value.duration,
        automaticRenewal: license.value.automaticRenewal,
        followupLicense: license.value.followupLicenseId ? `/licenses/${license.value.followupLicenseId}` : undefined,
        licenseSeats: license.value.licenseSeats?.map((seat) => ({
          id: seat.id,
          type: seat.type,
          seats: seat.seats,
        })),
        licenseGoal: {
          id: license.value.licenseGoal?.id ?? undefined,
          formula: license.value.licenseGoal?.formula,
          licenseGoalStatements: writeGoalStatements(license.value.licenseGoal?.licenseGoalStatements),
        },
        licenseRanking: {
          id: license.value.licenseRanking?.id ?? undefined,
          licenseRankingStatements: writeRankingStatements(license.value.licenseRanking?.licenseRankingStatements),
        },
        licenseReward: {
          id: license.value.licenseReward?.id ?? undefined,
          licenseBounties: license.value.licenseReward?.licenseBounties?.map((bounty) => ({
            rewardType: bounty.rewardType,
            amount: bounty.amount,
          })),
        },
        payoutSchedule: {
          id: license.value.payoutSchedule?.id ?? undefined,
          type: license.value.payoutSchedule?.type,
          interval: license.value.payoutSchedule?.type === 'monthly' ? license.value.payoutSchedule?.interval : null,
          minimumThreshold: license.value.payoutSchedule?.minimumThreshold,
          maximumPayout: license.value.payoutSchedule?.maximumPayout,
        },
        advertisementEnabled: license.value.advertisementEnabled,
        blockedAdvertisementCompanies: license.value.blockedAdvertisementCompanies?.map((company) => company.value),
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>

<style lang="scss" scoped></style>
